import React, { useContext, useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Loading from "../../Loading";
import VND from "../../components/VND";
import { wdays } from "../../../../enums";
import { StaffContext } from "../../../../Context";
import mut from "../../hooks/mut";
import useDB from "../../hooks/useDB";
import Error from "../../components/Error";
import {
  User,
  Mail,
  Shield,
  DollarSign,
  CalendarDays,
  Plus,
  X,
  ChevronDown,
} from "lucide-react";

/**
 * StaffMember — Detail page for a single staff member at /manage/staff/:id.
 * Shows name, login, role management (chip-based), salary editing,
 * and day-off toggles. Fully custom — no MUI.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 2.0
 * @param {Object} props
 * @param {Object} props.center - Center data with available roles
 * @returns {React.ReactElement}
 */
export default function StaffMember(props) {
  const { id } = useParams();
  const [staff, setStaff, error, loading] = useDB(`/user/${id}`);
  const { update } = useContext(StaffContext);

  if (loading) return <Loading />;
  if (error) return <Error />;

  /**
   * Add or remove a role from the staff member and persist to server.
   * @param {string} role - Role name to toggle
   */
  const toggleRole = (role) => {
    const roles = staff.roles.includes(role)
      ? staff.roles.filter((r) => r !== role)
      : [...staff.roles, role];
    mut("post", `/user/${staff._id}`, { roles });
    const updated = { ...staff, roles };
    setStaff(updated);
    update(updated);
  };

  /**
   * Deterministic avatar background color from a string.
   * @param {string} str - Input string
   * @returns {string} Tailwind bg class
   */
  const avatarColor = (str) => {
    const colors = [
      "bg-sky-100 text-sky-600",
      "bg-emerald-100 text-emerald-600",
      "bg-violet-100 text-violet-600",
      "bg-amber-100 text-amber-600",
      "bg-rose-100 text-rose-600",
      "bg-indigo-100 text-indigo-600",
      "bg-teal-100 text-teal-600",
      "bg-orange-100 text-orange-600",
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const availableRoles = (props.center?.roles || []).filter(
    (r) => !staff.roles.includes(r)
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
      {/* Header Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mb-4">
        <div className="flex items-center gap-4">
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 ${avatarColor(staff.name)}`}
          >
            {getInitials(staff.name)}
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">{staff.name}</h1>
            <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-0.5">
              <Mail size={14} />
              {staff.login}
            </div>
          </div>
        </div>
      </div>

      {/* Roles Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={18} className="text-teal-600" />
          <h2 className="text-base font-bold text-slate-800">Roles</h2>
        </div>

        <div className="flex flex-wrap gap-2">
          {staff.roles.map((role) => (
            <button
              key={role}
              onClick={() => toggleRole(role)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-600 border border-teal-200 rounded-lg text-sm font-semibold hover:opacity-80 transition-all duration-150 cursor-pointer"
            >
              {role}
              <X size={12} />
            </button>
          ))}

          {availableRoles.length > 0 && (
            <RoleDropdown
              availableRoles={availableRoles}
              onAdd={toggleRole}
            />
          )}
        </div>
      </div>

      {/* Salary + Days Off Row */}
      <div className="flex gap-4 mb-4">
        {/* Salary Card */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex-1">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-800">Salary</h2>
          </div>
          <DrawSalary staff={staff} setStaff={setStaff} />
        </div>

        {/* Days Off Card */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex-1">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-800">
              Days Off ({staff.daysOff ? staff.daysOff.length : 0}/week)
            </h2>
          </div>
          <DayOff staff={staff} setStaff={setStaff} />
        </div>
      </div>

      {/* Reports */}
      {staff.reports && staff.reports.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <User size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-slate-800">
              Direct Reports ({staff.reports.length})
            </h2>
          </div>
          <div className="flex flex-col gap-2">
            {staff.reports.map((report) => (
              <Link
                key={report._id}
                to={`/manage/staff/${report._id}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 hover:text-teal-600"
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${avatarColor(report.name)}`}>
                  {getInitials(report.name)}
                </div>
                {report.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DrawSalary — Inline salary editor with auto-save on blur.
 * @param {Object} props
 * @param {Object} props.staff - Staff data object
 * @param {Function} props.setStaff - State setter
 * @returns {React.ReactElement}
 */
function DrawSalary({ staff, setStaff }) {
  const updateSalary = () => {
    mut("post", `/user/${staff._id}`, { salary: staff.salary });
  };

  return (
    <VND
      value={staff.salary || ""}
      setValue={(salary) => setStaff((prev) => ({ ...prev, salary }))}
      onBlur={updateSalary}
    />
  );
}

/**
 * DayOff — Toggle buttons for each day of the week.
 * Click to add/remove a day off. Persists immediately.
 *
 * @param {Object} props
 * @param {Object} props.staff - Staff data with daysOff array
 * @param {Function} props.setStaff - State setter
 * @returns {React.ReactElement}
 */
function DayOff({ staff, setStaff }) {
  const { updateStaff } = useContext(StaffContext);

  return (
    <div className="flex gap-2">
      {wdays.map((w) => {
        const isActive = staff.daysOff && staff.daysOff.includes(w.day);
        return (
          <button
            key={w.day}
            className={`w-12 h-10 rounded-lg text-sm font-bold transition-all duration-150 cursor-pointer ${
              isActive
                ? "bg-red-100 text-red-600 border border-red-200 hover:bg-red-200"
                : "bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100"
            }`}
            onClick={() => {
              let daysOff = staff.daysOff || [];
              if (daysOff.includes(w.day)) {
                daysOff = daysOff.filter((d) => d !== w.day);
              } else {
                daysOff.push(w.day);
              }
              setStaff((prev) => ({ ...prev, daysOff }));
              mut("post", `/user/${staff._id}`, { daysOff });
              updateStaff({ ...staff, daysOff });
            }}
          >
            {w.name}
          </button>
        );
      })}
    </div>
  );
}

/**
 * RoleDropdown — Dropdown to add a role from the list of unassigned roles.
 * Uses click-outside detection instead of MUI Autocomplete.
 *
 * @param {Object} props
 * @param {string[]} props.availableRoles - Roles not yet assigned
 * @param {Function} props.onAdd - Callback when a role is selected
 * @returns {React.ReactElement}
 */
function RoleDropdown({ availableRoles, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-100 hover:border-slate-300 transition-all duration-150 cursor-pointer"
      >
        <Plus size={14} />
        Add role
        <ChevronDown size={12} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px]">
          {availableRoles.map((role) => (
            <button
              key={role}
              onClick={() => {
                onAdd(role);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-teal-600 transition-colors cursor-pointer"
            >
              {role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
